import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, AlertTriangle, Settings, RefreshCw } from 'lucide-react';

/**
 * 📍 Location Permission Modal
 * 
 * A BLOCKING modal that REQUIRES users to enable location services.
 * Users CANNOT proceed without enabling location.
 */
const LocationPermissionModal = ({ onLocationGranted }) => {
  const [permissionStatus, setPermissionStatus] = useState('checking'); // checking, denied, unavailable, granted, prompting
  const [isRetrying, setIsRetrying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const requestLocation = useCallback(() => {
    setIsRetrying(true);
    setErrorMessage('');
    setPermissionStatus('prompting');
    
    // Request location - this should trigger the browser prompt
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('📍 Location granted:', position.coords.latitude, position.coords.longitude);
        setPermissionStatus('granted');
        setIsRetrying(false);
        // Call the callback to close the modal
        if (onLocationGranted) {
          onLocationGranted();
        }
      },
      (error) => {
        console.error('📍 Location error:', error);
        setIsRetrying(false);
        if (error.code === error.PERMISSION_DENIED) {
          setPermissionStatus('denied');
          setErrorMessage('Location permission was denied. Please enable it in your browser settings.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setPermissionStatus('unavailable');
          setErrorMessage('Location information is unavailable. Please check your device settings.');
        } else if (error.code === error.TIMEOUT) {
          setPermissionStatus('denied');
          setErrorMessage('Location request timed out. Please try again.');
        } else {
          setPermissionStatus('denied');
          setErrorMessage('Could not get your location. Please try again.');
        }
      },
      { 
        enableHighAccuracy: false, 
        timeout: 15000, 
        maximumAge: 0 // Don't use cached position
      }
    );
  }, [onLocationGranted]);

  useEffect(() => {
    const checkLocationPermission = async () => {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        setPermissionStatus('unavailable');
        setErrorMessage('Your browser does not support location services.');
        return;
      }

      // Check permission status using Permissions API
      if (navigator.permissions) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          console.log('📍 Permission state:', result.state);
          
          if (result.state === 'granted') {
            // Already granted - verify by getting position
            setPermissionStatus('granted');
            if (onLocationGranted) {
              onLocationGranted();
            }
          } else if (result.state === 'denied') {
            setPermissionStatus('denied');
            setErrorMessage('Location permission is blocked. Please enable it in your browser settings.');
          } else {
            // 'prompt' state - user hasn't decided yet
            // Automatically request location
            requestLocation();
          }

          // Listen for permission changes
          result.addEventListener('change', () => {
            console.log('📍 Permission changed to:', result.state);
            if (result.state === 'granted') {
              setPermissionStatus('granted');
              if (onLocationGranted) {
                onLocationGranted();
              }
            } else if (result.state === 'denied') {
              setPermissionStatus('denied');
              setErrorMessage('Location permission was denied. Please enable it in your browser settings.');
            }
          });
        } catch (err) {
          console.log('📍 Permissions API error, trying direct request:', err);
          // Permissions API not fully supported, try direct request
          requestLocation();
        }
      } else {
        // No Permissions API, try direct request
        requestLocation();
      }
    };

    checkLocationPermission();
  }, [onLocationGranted, requestLocation]);

  // Don't show modal if permission is granted
  if (permissionStatus === 'granted') {
    return null;
  }

  // Show loading state while checking
  if (permissionStatus === 'checking') {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4">
        <div className="bg-gray-900 rounded-2xl border border-gray-700 max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-orange-600/20 flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="h-8 w-8 text-orange-500 animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Checking Location...</h2>
          <p className="text-gray-400">Please wait while we check your location permissions.</p>
        </div>
      </div>
    );
  }

  // Show prompting state when browser dialog should be showing
  if (permissionStatus === 'prompting' && isRetrying) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4">
        <div className="bg-gray-900 rounded-2xl border border-gray-700 max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-orange-600/20 flex items-center justify-center mx-auto mb-4">
            <MapPin className="h-8 w-8 text-orange-500 animate-pulse" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Allow Location Access</h2>
          <p className="text-gray-400 mb-4">Please click <strong className="text-white">"Allow"</strong> in the browser popup to continue.</p>
          <div className="flex items-center justify-center gap-2 text-orange-400">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Waiting for permission...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 max-w-md w-full p-8 text-center">
        {/* Icon */}
        <div className="w-20 h-20 rounded-full bg-orange-600/20 flex items-center justify-center mx-auto mb-6">
          <MapPin className="h-10 w-10 text-orange-500" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-white mb-4">
          Location Required
        </h2>

        {/* Message based on status */}
        {permissionStatus === 'denied' && (
          <>
            <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-red-400 mb-2">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-semibold">Location Access Required</span>
              </div>
              <p className="text-gray-300 text-sm">
                {errorMessage || 'HopSeat requires location services to show nearby gigs, calculate routes, and provide accurate tour tracking.'}
              </p>
            </div>

            <div className="bg-gray-800 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                How to Enable Location:
              </h3>
              <ol className="text-sm text-gray-400 space-y-2">
                <li>1. Click the <strong className="text-white">lock/site settings icon</strong> in your browser&apos;s address bar</li>
                <li>2. Find <strong className="text-white">&quot;Location&quot;</strong> in the permissions list</li>
                <li>3. Change from &quot;Block&quot; to <strong className="text-white">&quot;Allow&quot;</strong></li>
                <li>4. <strong className="text-white">Refresh the page</strong> or click the button below</li>
              </ol>
            </div>
          </>
        )}

        {permissionStatus === 'unavailable' && (
          <>
            <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-yellow-400 mb-2">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-semibold">Location Unavailable</span>
              </div>
              <p className="text-gray-300 text-sm">
                {errorMessage || 'Your browser or device does not support location services. Please try using a different browser (Chrome, Firefox, Safari) or enable location on your device.'}
              </p>
            </div>
          </>
        )}

        {/* Action Button */}
        <button
          onClick={requestLocation}
          disabled={isRetrying}
          className="w-full px-6 py-4 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-lg"
        >
          {isRetrying ? (
            <>
              <RefreshCw className="h-5 w-5 animate-spin" />
              Checking Location...
            </>
          ) : (
            <>
              <MapPin className="h-5 w-5" />
              Enable Location & Continue
            </>
          )}
        </button>

        {/* Refresh hint */}
        <p className="text-xs text-gray-500 mt-4">
          If you&apos;ve enabled location in browser settings, <button onClick={() => window.location.reload()} className="text-orange-400 hover:underline">refresh the page</button>.
        </p>
      </div>
    </div>
  );
};

export default LocationPermissionModal;
